import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import { SmartChargeTimeline } from '../RichCalculators';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function HvadErDK1DK2({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad er DK1 og DK2?', answer: 'DK1 og DK2 er Danmarks to elprisområder (budzoner). DK1 dækker Vestdanmark – Jylland og Fyn. DK2 dækker Østdanmark – Sjælland, Lolland-Falster og Bornholm. Storebælt deler de to zoner. Hver zone har sin egen spotpris time for time.' },
    { question: 'Hvilken zone bor jeg i?', answer: 'Bor du i Jylland eller på Fyn, er du i DK1. Bor du på Sjælland, Lolland-Falster eller Bornholm, er du i DK2. Det er din adresse – ikke dit elselskab – der afgør zonen.' },
    { question: 'Hvorfor er der to elzoner i Danmark?', answer: 'Fordi der er begrænset kabelkapacitet mellem Øst- og Vestdanmark (over Storebælt). Når der ikke kan transporteres nok strøm imellem zonerne, opstår der forskellige priser. DK1 hænger tæt sammen med det tyske og norske marked, mens DK2 er mere koblet til Sverige.' },
    { question: 'Er strømmen dyrere i DK1 eller DK2?', answer: 'Det varierer time for time og dag for dag. Historisk er DK1 ofte en anelse billigere på grund af mere vindkraft i Vestdanmark, men forskellen er typisk lille og kan vende. Over et helt år er gennemsnittene tæt på hinanden.' },
    { question: 'Betyder zonen noget for mit valg af elselskab?', answer: 'Spotprisen følger din zone uanset elselskab. Det du selv vælger er tillægget og abonnementet. Hos enkel energi betaler du spotpris + 0 øre i tillæg i begge zoner – så du betaler altid den faktiske markedspris for din zone.' },
    { question: 'Kan jeg vælge en anden zone for at få billigere strøm?', answer: 'Nej. Zonen følger din fysiske adresse og kan ikke vælges. Men du kan reducere din effektive pris ved at flytte forbrug til de billige timer – fx med Saving Mode til elbil-opladning.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'kort', label: 'Zonerne på kort' },
    { id: 'hvorfor', label: 'Hvorfor to zoner' },
    { id: 'pris', label: 'Prisforskelle' },
    { id: 'betydning', label: 'Hvad betyder det for dig' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Hvad er DK1 og DK2?', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Hvad er DK1 og DK2?" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="El forklaret · 2026"
        h1="Hvad er DK1 og DK2? Danmarks to elzoner forklaret"
        intro="Danmark er delt i to elprisområder. Her får du en helt enkel forklaring på, hvad DK1 og DK2 er, hvilken zone du bor i, hvorfor de findes – og hvad det betyder for din elpris."
        badges={['DK1 = Vestdanmark', 'DK2 = Østdanmark', 'Spotpris + 0 øre i begge zoner']}
        updated={updated}
        image="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: 'DK1', label: 'Jylland + Fyn (Vestdanmark)' },
          { value: 'DK2', label: 'Sjælland m.fl. (Østdanmark)' },
          { value: 'Storebælt', label: 'deler de to zoner' },
          { value: '0 øre', label: 'tillæg hos enkel – begge zoner' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="DK1 og DK2 – kort forklaret">
        <AnswerBox question="Hvad er DK1 og DK2?">
          DK1 og DK2 er Danmarks to <strong>elprisområder</strong> (budzoner). <strong>DK1 = Vestdanmark</strong> (Jylland og Fyn) og
          <strong> DK2 = Østdanmark</strong> (Sjælland, Lolland-Falster og Bornholm). De er delt af Storebælt, fordi der er begrænset
          kabelkapacitet imellem dem. Hver zone har sin egen spotpris time for time. Hos enkel energi betaler du spotpris + 0 øre tillæg uanset zone.
        </AnswerBox>
        <p>
          Når du ser elpriser i nyheder eller apps, vil du ofte se to tal: ét for DK1 og ét for DK2. Det skyldes, at det danske elmarked
          er delt op i to prisområder. Strømmen er fysisk den samme, men prisen kan variere, fordi udbud og efterspørgsel er forskelligt i øst og vest.
        </p>
      </ProseSection>

      <ProseSection id="kort" heading="Hvilken zone bor du i?" alt>
        <div className="my-6">
          <ComparisonTable
            caption="Oversigt over DK1 og DK2"
            columns={[{ name: 'DK1 – Vestdanmark', highlight: true }, { name: 'DK2 – Østdanmark' }]}
            rows={[
              { label: 'Landsdele', values: ['Jylland og Fyn', 'Sjælland, Lolland-Falster, Bornholm'] },
              { label: 'Folketal (ca.)', values: ['~3,7 mio.', '~2,7 mio.'] },
              { label: 'Stærkest koblet til', values: ['Tyskland og Norge', 'Sverige'] },
              { label: 'Større byer', values: ['Aarhus, Aalborg, Odense, Esbjerg', 'København, Roskilde, Næstved'] },
              { label: 'enkel-tillæg til spotpris', values: ['0 øre', '0 øre'] },
            ]}
          />
        </div>
        <p>Det er din <strong>adresse</strong> – ikke dit elselskab – der afgør, hvilken zone du hører til. Du kan ikke selv vælge zone.</p>
      </ProseSection>

      <ProseSection id="hvorfor" heading="Hvorfor er Danmark delt i to elzoner?">
        <p>
          Årsagen er fysisk infrastruktur. Der går kun et begrænset antal højspændingskabler over Storebælt mellem Øst- og Vestdanmark.
          Når der ikke kan transporteres nok strøm fra den billige zone til den dyre, "splittes" prisen, og de to områder får hver deres spotpris.
        </p>
        <p>
          DK1 er tæt forbundet med det store nordtyske og norske marked, mens DK2 er mere koblet til Sverige. Derfor afspejler de to zoner
          forskellige nabolandes produktion og forbrug. Når det blæser kraftigt i Vestjylland, kan DK1 blive markant billigere end DK2 i de timer.
        </p>
        <FactGrid columns={3} facts={[
          { value: 'Nord Pool', label: 'Den fælles nordiske elbørs', sub: 'Sætter spotprisen time for time' },
          { value: 'Storebælt', label: 'Flaskehals mellem zonerne', sub: 'Begrænset kabelkapacitet' },
          { value: 'Time for time', label: 'Priserne opdateres', sub: '24 priser i døgnet pr. zone' },
        ]} />
      </ProseSection>

      <ProseSection id="pris" heading="Er strømmen dyrere i DK1 eller DK2?" alt>
        <p>
          Det svinger. Historisk er DK1 ofte en anelse billigere på grund af den store mængde vindkraft i Vestdanmark, men forskellen er
          typisk lille og kan vende fra time til time. Over et helt år ender gennemsnittene tæt på hinanden. Det vigtigste er ikke <em>hvilken</em>
          zone du er i, men <em>hvornår</em> du bruger strømmen.
        </p>
        <div className="my-6"><SmartChargeTimeline /></div>
        <p>
          Uanset zone gælder samme mønster: strømmen er typisk billigst om natten og dyrest i aftenspidsen kl. 17–20. Flytter du forbrug
          til de billige timer, sparer du – uanset om du er i DK1 eller DK2.
        </p>
      </ProseSection>

      <ProseSection id="betydning" heading="Hvad betyder zonen for dit valg af elselskab?">
        <AnswerBox>
          Spotprisen følger din zone <strong>uanset elselskab</strong>. Det du selv vælger, er tillægget pr. kWh og abonnementet. Hos enkel
          energi er tillægget <strong>0 øre i både DK1 og DK2</strong> – så du betaler altid den faktiske markedspris for din zone, plus et fast
          abonnement fra 18 kr./md.
        </AnswerBox>
        <p>
          Mange selskaber lægger 2–8 øre tillæg pr. kWh oveni spotprisen i begge zoner. Det er her, du kan spare. Og har du elbil, kan du
          reducere din effektive opladningspris yderligere med Saving Mode (automatisk opladning i de billige timer) og laderabat på 10 øre/kWh på hjemmeopladning styret af Saving Mode i enkel-appen (kompatibel ladeboks og bil kræves).
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/sammenlign/gennemsnitlig-elpris-2026', label: 'Gennemsnitlig elpris 2026' },
        { path: '/saadan-fungerer-el-markedet', label: 'Sådan fungerer elmarkedet' },
        { path: '/forstaa-din-elregning', label: 'Forstå din elregning' },
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Spotpris uden tillæg – uanset zone" subtext="DK1 eller DK2, du betaler altid den faktiske markedspris hos enkel. Fra 18 kr./md., ingen binding." />
      <SEODisclaimer page={page} />
    </article>
  );
}